import { useEffect, useRef } from "react";
import {
  useFetchOrganizationInvitations,
  useCreateOrganizationInvitation,
} from "@core/features/organization";
import {
  INVITATION_TYPE,
  useFetchInvitationTemplates,
} from "@core/features/invitation";
import { Loader } from "@themes/pollinator/ui/Loader";
import { OrgOutreachTab } from "./OrgOutreachTab";
import type { Organization } from "@core/features/organization";

type Props = {
  organization: Organization;
  programId: number;
};

/**
 * Container for OrgOutreachTab that fetches invitations.
 * Filters to get participant invitations for the outreach tab.
 * Auto-creates participant invitation if none exists.
 */
export function OrgOutreachTabContainer({ organization, programId }: Props) {
  const hasAttemptedCreate = useRef(false);

  const { data: invitations, isLoading } = useFetchOrganizationInvitations({
    organizationId: organization.id,
  });

  const { data: templates, isLoading: isLoadingTemplates } =
    useFetchInvitationTemplates({
      programId,
      type: INVITATION_TYPE.PARTICIPANT,
    });

  const { mutate: createInvitation } = useCreateOrganizationInvitation({
    organizationId: organization.id,
  });

  // Filter for participant invitations
  const participantInvitations =
    invitations?.filter((inv) => inv.type === INVITATION_TYPE.PARTICIPANT) ??
    [];

  const hasParticipantInvitation = participantInvitations.length > 0;

  // Get the first participant template
  const participantTemplate = templates?.[0] ?? null;

  // Auto-create participant invitation if none exists and template is available
  useEffect(() => {
    if (
      !isLoading &&
      !isLoadingTemplates &&
      invitations &&
      !hasParticipantInvitation &&
      participantTemplate &&
      !hasAttemptedCreate.current
    ) {
      hasAttemptedCreate.current = true;
      createInvitation({
        programId,
        templateId: participantTemplate.id,
        type: INVITATION_TYPE.PARTICIPANT,
      });
    }
  }, [
    isLoading,
    isLoadingTemplates,
    invitations,
    hasParticipantInvitation,
    participantTemplate,
    createInvitation,
    programId,
  ]);

  if (
    isLoading ||
    isLoadingTemplates ||
    (!hasParticipantInvitation && !hasAttemptedCreate.current)
  ) {
    return (
      <div className="flex justify-center py-12">
        <Loader />
      </div>
    );
  }

  return (
    <OrgOutreachTab
      organization={organization}
      invitations={participantInvitations}
      programId={programId}
      templateId={participantTemplate?.id ?? null}
    />
  );
}
